import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Highlighter from 'react-highlight-words';
import {useNavigate} from 'react-router-dom';
import {debounce} from 'lodash';
import {LIMIT_PAYLOAD, ROUTES} from 'common/constants';

import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';
import {
	AutoSuggest,
	CloseIcon,
	Divider,
	IconButton,
	Layout,
	ListItem,
	Page,
	SearchIcon,
	Tabs,
	Text,
} from '@phonectas/unifon-ui-kit';
// import HistoryTab from './dashboard/HistoryTab';
import ResponsibleTab from './dashboard/ResponsibleTab';
import {isValidPhone} from 'utils/phoneNumber';
import {serviceStatusesColor} from 'utils/div';
import {DATE_FORMATS, dateByCustomFormat} from 'utils/date';
import {Accounts} from 'common/types/store/threeSixtyTypes';

const ThreeSixtyDashboard: React.FC = () => {
	const [t] = useTranslation();
	const navigate = useNavigate();
	const {
		getAccountList,
		majorResponsibleList: majorResponsibleListNew,
		getUser,
		resetAccounts,
	} = useThreeSixtyNewStore((state) => ({
		getAccountList: state.getAccountList,
		majorResponsibleList: state.majorResponsibleList,
		getUser: state.getUser,
		resetAccounts: state.resetAccounts,
	}), shallow);
	const [value, setValue] = useState('');
	const accountsList = useThreeSixtyNewStore((state) => state.accounts);
	const accounts = accountsList?.accounts;
	const [activeTab, setActiveTab] = useState('1');
	const tabs = useMemo(() => [
		{value: '1', label: t('Customer responsible').toUpperCase()},
		// {value: '2', label: t('SMB').toUpperCase()},
		// {value: '3', label: t('Statistics').toUpperCase()},
	], [t]);
	const debouncedFetchList = useMemo(() => debounce((searchValue) => {
		getAccountList(10, searchValue, 'serviceStatusAsc');
	}, 500), [getAccountList]);

	// Drop a search still waiting in the debounce, and the results of the old one
	const handleClearSearch = useCallback(() => {
		setValue('');
		debouncedFetchList.cancel();
		resetAccounts();
	}, [debouncedFetchList, resetAccounts]);

	useEffect(() => () => debouncedFetchList.cancel(), [debouncedFetchList]);

	const handleChange = useCallback(
		 
		(val: string | number | string[] | number[] | null | undefined, _e: React.SyntheticEvent, _name: string) => {
			if (typeof val === 'string') {
				setValue(val);
				debouncedFetchList(val);
			} else if (typeof val === 'number') {
				setValue(val.toString());
				debouncedFetchList(val.toString());
			} else if (Array.isArray(val) && val.length > 0) {
				const first = val[0];
				if (typeof first === 'string') {
					setValue(first);
					debouncedFetchList(first);
				} else if (typeof first === 'number') {
					setValue(first.toString());
					debouncedFetchList(first.toString());
				}
			} else {
				handleClearSearch();
			}
		},
		[debouncedFetchList, handleClearSearch]
	);

	const onClick = useCallback((item: string) => {
		if (isValidPhone(value)) {
			getUser({
				phone: value,
				accountId: item,
				onSuccess: () => {
					navigate(`${ROUTES.THREE_SIXTY}/${item}`);
				}
			});
		} else {
			navigate(`${ROUTES.THREE_SIXTY}/${item}`);
		}
	}, [navigate, getUser, value]);

	const renderOption = useCallback(
		({
			index,
			style,
			data: {collection},
		}: {
			index: number;
			style: React.CSSProperties;
			data: {collection: Accounts[]};
		}) => {
			if (!collection || !collection[index]) {
				return <div key={index}>No data</div>
			}
			const item = collection[index];
			const userPhone = item?.user_details?.phone_number;
			const userName = item?.user_details?.user_name;
			const isUser = item?.user_details?.is_user;
			const companyName = item?.name;
			const endDate = item?.user_details?.end_date;
			const status = item?.user_details?.service_status?.toString() || '';

			return (
				<ListItem
					key={index}
					value={item.id}
					onClick={() => onClick(item.id)}
					style={style}
					primaryText={
						<Layout nowrap fullWidth justifyContent="space-between">
							<Text variant="h5">
								<Highlighter
									autoEscape
									searchWords={[value]}
									textToHighlight={isUser ? userPhone : companyName ? companyName : ''}
								/>
							</Text>
							{
								endDate && (
									<Text>
										{dateByCustomFormat(endDate, DATE_FORMATS.DD_MM_YY)}
									</Text>
								)
							}
						</Layout>
					}
					secondaryText={
						<>
							{isUser ? (
								<Layout fullWidth justifyContent="space-between" nowrap>
									<Text color="textSecondary" variant="caption">
										{item.name && userName ? `${userName} - ${item.name}` : item.name}
									</Text>
									<Text
										style={{
											color: serviceStatusesColor(status).priColor,
										}}
										variant="caption"
									>
										{serviceStatusesColor(status).text}
									</Text>
								</Layout>
							) : (
								<Text color="textSecondary" variant="caption">
									{`${t('VAT')}: ${item.vat_number}`}
								</Text>
							)}
						</>
					}
				/>
			);
		},
		[value, onClick, t]
	);

	const handleTabSwitch = useCallback(
		(tab: string | number, _event: React.SyntheticEvent) => {
			setActiveTab(String(tab));
		},
		[]
	);

	useEffect(() => {
		majorResponsibleListNew({limit: LIMIT_PAYLOAD});
	}, [majorResponsibleListNew]); 

	return (
		<Page style={{overflow: 'hidden'}}>
			<Layout style={{rowGap: '5px'}} direction="column" fullWidth>
				<Layout fullWidth alignItems="center" nowrap style={{height: '40px'}}>
					<Text style={{fontSize: '30px'}} variant="h1">Unifon 360</Text>
				</Layout>
			</Layout>
			<Layout direction="column" nowrap fullHeight fullWidth style={{marginTop: '20px', overflow: 'hidden'}}>
				<Layout style={{width: '350px'}}>
					<AutoSuggest
						fullWidth
						value={value}
						placeholder={t('Search for customer')}
						itemSize={53}
						onChange={handleChange}
						disableFiltering
						collection={accounts || []}
						renderItem={renderOption}
						endAdornment={
							!value ? (
								<IconButton>
									<SearchIcon />
								</IconButton>
							) : (
								<IconButton onClick={handleClearSearch}>
									<CloseIcon />
								</IconButton>
							)
						}
						maxDropdownHeight={300}
						inputProps={{autoComplete: 'off'}}
					/>
				</Layout>
				<Divider dark />
				<Tabs
					items={tabs}
					active={activeTab}
					onChange={handleTabSwitch}
				/>
				{activeTab === '1' && <ResponsibleTab />}
				{activeTab === '2' && <div>statistikk</div>}
			</Layout>
		</Page>
	);
}

export default ThreeSixtyDashboard;
